import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, Paperclip, X } from 'lucide-react';
import { chatAPI, uploadAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import MediaUpload from './MediaUpload';
import toast from 'react-hot-toast';

const ChatWindow = ({ selectedModel, chatId, onChatCreated }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(chatId);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { user, updateUser } = useAuth();

  // Load chat when chatId changes
  useEffect(() => {
    if (chatId && chatId !== currentChatId) {
      loadChat(chatId);
    } else if (!chatId) {
      // New chat
      setMessages([]);
      setCurrentChatId(null);
    }
  }, [chatId, currentChatId]);

  const loadChat = async (id) => {
    try {
      const response = await chatAPI.getChat(id);
      if (response.data.success) {
        setMessages(response.data.chat.messages || []);
        setCurrentChatId(id);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
      toast.error('Failed to load chat');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleFilesSelected = (files) => {
    setAttachedFiles(files);
  };

  const uploadFiles = async (files) => {
    if (files.length === 0) return [];
    
    try {
      const fileObjects = files.map(f => f.file);
      const response = await uploadAPI.uploadFiles(fileObjects);
      
      if (response.data.success) {
        toast.success('Files uploaded successfully');
        return response.data.files;
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload files');
      return [];
    }
  };

  const removeAttachedFile = (fileId) => {
    const updatedFiles = attachedFiles.filter(f => f.id !== fileId);
    setAttachedFiles(updatedFiles);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() && attachedFiles.length === 0) return;
    
    if (user?.tokens <= 0) {
      toast.error("You've run out of tokens!");
      return;
    }

    setLoading(true);

    // Upload files first if any
    let uploadedFiles = [];
    if (attachedFiles.length > 0) {
      uploadedFiles = await uploadFiles(attachedFiles);
    }

    const userMessage = {
      role: 'user',
      text: inputMessage,
      timestamp: new Date(),
      attachments: uploadedFiles
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setAttachedFiles([]);
    setShowUpload(false);

    try {
      const response = await chatAPI.sendMessage(selectedModel, {
        message: inputMessage.trim(),
        chatId: currentChatId
      });

      if (response.data.success) {
        const assistantMessage = {
          role: 'assistant',
          text: response.data.aiResponse,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, assistantMessage]);
        
        if (!currentChatId) {
          setCurrentChatId(response.data.chat.id);
          onChatCreated?.(response.data.chat.id);
        }

        updateUser({ tokens: response.data.tokensRemaining });
        
        // Focus input on mobile after sending
        if (window.innerWidth <= 768) {
          setTimeout(() => inputRef.current?.focus(), 100);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.message || 'Failed to send message');
      
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 scrollbar-hide">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
              <Bot className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              Start a conversation
            </h3>
            <p className="text-sm sm:text-base text-gray-600 max-w-md leading-relaxed">
              Choose an AI model and start chatting. Each message costs 1 token.
              You have <span className="font-medium">{user?.tokens || 0}</span> tokens remaining.
            </p>
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[85%] sm:max-w-3xl ${
                message.role === 'user' 
                  ? 'bg-primary-50 border border-primary-200 rounded-2xl rounded-br-md' 
                  : 'bg-gray-50 border border-gray-200 rounded-2xl rounded-bl-md'
              } p-3 sm:p-4 animate-slide-up`}>
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user' 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-gray-600 text-white'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="w-3 h-3 sm:w-4 sm:h-4" />
                    ) : (
                      <Bot className="w-3 h-3 sm:w-4 sm:h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-xs sm:text-sm text-gray-900">
                        {message.role === 'user' ? 'You' : selectedModel.charAt(0).toUpperCase() + selectedModel.slice(1)}
                      </span>
                      <span className="text-xs text-gray-500 hidden sm:inline">
                        {formatTimestamp(message.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm sm:text-base text-gray-800 whitespace-pre-wrap leading-relaxed break-words">
                      {message.text}
                    </p>
                    
                    {/* File Attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {message.attachments.map((file, fileIndex) => (
                          <div key={fileIndex} className="flex items-center space-x-2 p-2 bg-white bg-opacity-50 rounded-lg border border-gray-200">
                            {file.category === 'image' ? (
                              <img
                                src={uploadAPI.getFileUrl(file.filename)}
                                alt={file.originalName}
                                className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded cursor-pointer"
                                onClick={() => window.open(uploadAPI.getFileUrl(file.filename), '_blank')}
                              />
                            ) : (
                              <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                                <Paperclip className="w-4 h-4 text-gray-600" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900 truncate">
                                {file.originalName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB • {file.category}
                              </p>
                            </div>
                            <button
                              onClick={() => window.open(uploadAPI.getFileUrl(file.filename), '_blank')}
                              className="text-xs text-primary-600 hover:text-primary-700"
                            >
                              View
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        
        {loading && (
          <div className="flex justify-start">
            <div className="max-w-[85%] sm:max-w-3xl bg-gray-50 border border-gray-200 rounded-2xl rounded-bl-md p-3 sm:p-4">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-600 text-white flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="font-medium text-xs sm:text-sm text-gray-900">
                      {selectedModel.charAt(0).toUpperCase() + selectedModel.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                    <span className="text-sm text-gray-500">Thinking...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 bg-white p-3 sm:p-4">
        {user?.tokens <= 0 && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 text-center">
              You've run out of tokens! Contact support to get more tokens.
            </p>
          </div>
        )}
        
        {/* File Upload Area */}
        {showUpload && (
          <div className="mb-4">
            <MediaUpload
              onFilesSelected={handleFilesSelected}
              maxFiles={5}
              maxSize={10 * 1024 * 1024}
            />
          </div>
        )}
        
        {/* Attached Files Preview */}
        {attachedFiles.length > 0 && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Attached Files ({attachedFiles.length})
              </span>
              <button
                onClick={() => setAttachedFiles([])}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Clear All
              </button>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {attachedFiles.map((fileObj) => (
                <div key={fileObj.id} className="flex items-center space-x-2 p-2 bg-white rounded border">
                  {fileObj.preview ? (
                    <img src={fileObj.preview} alt={fileObj.file.name} className="w-8 h-8 object-cover rounded" />
                  ) : (
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                      <Paperclip className="w-4 h-4 text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{fileObj.file.name}</p>
                    <p className="text-xs text-gray-500">{(fileObj.file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button
                    onClick={() => removeAttachedFile(fileObj.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-3 h-3 text-gray-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <form onSubmit={handleSendMessage} className="flex items-end space-x-2 sm:space-x-3">
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder="Type your message..."
              disabled={loading || user?.tokens <= 0}
              rows={1}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              style={{
                minHeight: '44px',
                maxHeight: '120px',
                overflowY: 'auto'
              }}
            />
          </div>
          
          {/* Attachment Button */}
          <button
            type="button"
            onClick={() => setShowUpload(!showUpload)}
            className={`flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 border-2 rounded-full transition-colors duration-200 flex-shrink-0 ${
              showUpload || attachedFiles.length > 0
                ? 'border-primary-500 bg-primary-50 text-primary-600'
                : 'border-gray-300 hover:border-gray-400 text-gray-500 hover:text-gray-600'
            }`}
            disabled={loading || user?.tokens <= 0}
          >
            <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          
          <button
            type="submit"
            disabled={loading || (!inputMessage.trim() && attachedFiles.length === 0) || user?.tokens <= 0}
            className="flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-full transition-colors duration-200 flex-shrink-0"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
          </button>
        </form>
        
        {/* Mobile hint */}
        <div className="mt-2 text-xs text-gray-500 text-center sm:hidden">
          Tap Enter to send • Shift+Enter for new line
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
